import { useQuery } from "@tanstack/react-query";
import { sendErrorReport } from "../../../utils/reporting";
import { fetchAirports } from "../../api/airports";
import { extractAirportData } from "../serializers/intake-funnel";
import { Airport } from "../types/intake-funnel";

const FIVE_MINUTES = 5 * 60 * 1000;

type AirportWithRank = Airport & { rank: number };

export function useAirportSearch(debouncedSearchTerm: string) {
  const isSearchStringCorrect = !!debouncedSearchTerm && debouncedSearchTerm.length >= 2;

  const { data, isError, isFetching } = useQuery<Airport[] | undefined, Error>({
    queryKey: [`api/airports?q=${debouncedSearchTerm}`],
    queryFn: async () => {
      if (!isSearchStringCorrect) return;

      try {
        const rawAirports = await fetchAirports(debouncedSearchTerm);
        const airports = rawAirports.map((rawAirport) => {
          return extractAirportData(rawAirport);
        });

        if (airports.some((airport) => !airport.rank)) {
          return airports;
        }

        const airportsWithRank = airports as AirportWithRank[];

        return airportsWithRank.sort((first, second) => second.rank - first.rank);
      } catch (error) {
        sendErrorReport(error, "Error on airports search");
      }
    },
    retry: 1,
    enabled: isSearchStringCorrect,
    staleTime: FIVE_MINUTES,
  });

  return {
    airports: data,
    isError,
    isFetching,
  };
}
